import { useState, useRef, KeyboardEvent } from "react";

interface MultiEmailInputProps {
  emails: string[];
  setEmails: React.Dispatch<React.SetStateAction<string[]>>;
}
export function MultiEmailInput({ emails, setEmails }: MultiEmailInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Email validation regex
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (["Enter", "Tab", ",", " "].includes(e.key)) {
      e.preventDefault();
      addEmail();
    } else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
      // Remove last email on backspace
      setEmails(emails.slice(0, -1));
    }
  };

  const addEmail = () => {
    if (!inputValue.trim()) return;

    const email = inputValue.trim().replace(/,$/, "");

    if (!isValidEmail(email)) {
      setError(`"${email}" is not a valid email`);
      return;
    }

    if (emails.includes(email)) {
      setError(`"${email}" is already added`);
      return;
    }

    setEmails([...emails, email]);
    setInputValue("");
    setError(null);
  };

  const removeEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const handleBlur = () => {
    if (inputValue) addEmail();
  };

  return (
    <div className="space-y-2">
      {/* <label
        htmlFor="email-input"
        className="block text-sm font-medium text-gray-700"
      >
        Recipients
      </label> */}

      <div
        className={`flex flex-wrap gap-2 items-center p-2 border rounded-md focus-within:ring-2 focus-within:ring-blue-500 ${
          error ? "border-red-500" : "border-gray-300"
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Email chips */}
        {emails.map((email, index) => (
          <div
            key={index}
            className="flex items-center bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full"
          >
            {email}
            <button
              type="button"
              onClick={() => removeEmail(index)}
              className="ml-1 text-blue-500 hover:text-blue-700"
            >
              &times;
            </button>
          </div>
        ))}

        {/* Input field */}
        <input
          ref={inputRef}
          id="email-input"
          type="email"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={emails.length === 0 ? "Enter emails..." : ""}
          className="flex-1 min-w-[100px] p-1 outline-none bg-transparent"
        />
      </div>

      {/* Error message */}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Helper text */}
      <p className="text-xs text-gray-500">
        Press Enter, Tab, or comma to add emails
      </p>
    </div>
  );
}
